import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useSquareCard } from "@/hooks/useSquareCard";

const SummaryCard = ({ ev, night, ticket, qty }) => {
  const subtotal = ticket.price * qty;
  return (
    <aside className="rounded-xl border border-[#E5DACA] bg-white p-6 sm:p-8" data-testid="checkout-summary">
      <p className="eyebrow">Your reservation</p>
      <h2 className="mt-3 font-display text-2xl text-[#1F1B17]">{ev.title}</h2>
      <dl className="mt-6 space-y-3 text-sm">
        <div className="flex justify-between gap-4"><dt className="text-[#6B6157]">Night</dt><dd className="text-right" data-testid="checkout-night">{night.label ? `${night.label} · ` : ""}{night.date_display}{night.time ? ` · ${night.time}` : ""}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-[#6B6157]">Seat</dt><dd className="text-right" data-testid="checkout-ticket">{ticket.name}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-[#6B6157]">Where</dt><dd className="text-right" data-testid="checkout-location">{[night.venue, night.city].filter(Boolean).join(", ") || ev.location}</dd></div>
      </dl>
      <dl className="mt-6 space-y-2 border-t border-[#E5DACA] pt-5 font-mono text-sm">
        <div className="flex justify-between text-[#6B6157]"><dt>{qty} × {money(ticket.price)}</dt><dd>{money(subtotal)}</dd></div>
        <div className="flex justify-between text-[#6B6157]"><dt>Checkout service fee</dt><dd>{money(ev.service_fee)}</dd></div>
        <div className="flex justify-between pt-2 text-lg text-[#1F1B17]"><dt>Total</dt><dd className="text-[#A8832F]" data-testid="checkout-total">{money(subtotal + ev.service_fee)}</dd></div>
      </dl>
      <Link to={`/events/${ev.slug}#book`} className="mt-6 inline-block text-xs uppercase tracking-widest text-[#A8832F] hover:text-[#8C6A22]" data-testid="checkout-change-link">Change selection</Link>
    </aside>
  );
};

export default function Checkout() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const card = useSquareCard(config);
  const qty = Math.max(1, Number(params.get("qty")) || 1);

  useEffect(() => {
    api.get(`/events/${params.get("event")}`).then((r) => setEv(r.data)).catch(() => setEv(false));
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, [params]);

  if (ev === false) return <p className="mx-auto max-w-3xl px-8 py-32 text-[#6B6157]" data-testid="checkout-error">That event couldn't be found.</p>;
  if (!ev) return <Skeleton className="mx-auto my-20 h-96 max-w-5xl" />;
  const night = ev.nights.find((n) => n.id === params.get("night"));
  const ticket = ev.ticket_types.find((t) => t.id === params.get("ticket"));
  if (!night || !ticket) return <p className="mx-auto max-w-3xl px-8 py-32 text-[#6B6157]" data-testid="checkout-error">Please <Link className="text-[#A8832F] underline" to={`/events/${ev.slug}`}>choose a night and seat</Link> first.</p>;

  const total = ticket.price * qty + ev.service_fee;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    if (!age) { toast.error("Please confirm every guest is 21 or older."); return; }
    setBusy(true);
    try {
      const [givenName, ...rest] = form.name.trim().split(" ");
      const token = await card.tokenize({
        amount: total.toFixed(2), currencyCode: "USD", intent: "CHARGE",
        customerInitiated: true, sellerKeyedIn: false,
        billingContact: { givenName, familyName: rest.join(" "), email: form.email },
      });
      const { data } = await api.post("/bookings", {
        event_slug: ev.slug, night_id: night.id, ticket_type_id: ticket.id, quantity: qty,
        ...form, age_confirmed: age, source_id: token, idempotency_key: crypto.randomUUID(),
      });
      navigate(`/booking/${data.code}?email=${encodeURIComponent(form.email)}&new=1`);
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.2fr_1fr]" data-testid="checkout-page">
      <form onSubmit={pay} className="space-y-8">
        <div>
          <p className="eyebrow">Checkout</p>
          <h1 className="mt-3 font-display text-4xl text-[#1F1B17]">Almost there.</h1>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label htmlFor="co-name">Full name</Label><Input id="co-name" required value={form.name} onChange={set("name")} className="mt-2 h-12" data-testid="checkout-name-input" /></div>
          <div><Label htmlFor="co-email">Email</Label><Input id="co-email" type="email" required value={form.email} onChange={set("email")} className="mt-2 h-12" data-testid="checkout-email-input" /></div>
          <div><Label htmlFor="co-phone">Phone (optional)</Label><Input id="co-phone" value={form.phone} onChange={set("phone")} className="mt-2 h-12" data-testid="checkout-phone-input" /></div>
        </div>
        <label className="flex cursor-pointer items-start gap-3 border border-[#E5DACA] rounded-lg bg-white p-5" data-testid="age-confirm-label">
          <Checkbox checked={age} onCheckedChange={(v) => setAge(!!v)} className="mt-0.5" data-testid="age-confirm-checkbox" />
          <span className="text-sm text-[#4A423A]"><ShieldCheck className="mr-1 inline h-4 w-4 text-[#A8832F]" />I confirm that every guest in my party is 21 or older and will bring a valid photo ID.</span>
        </label>
        <div>
          <Label className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-[#A8832F]" />Card details</Label>
          {config?.enabled ? (
            <div id="square-card" className="mt-3 min-h-[90px]" data-testid="square-card-container" />
          ) : config && (
            <p className="mt-3 border border-[#E5DACA] bg-[#F6EEDC] p-4 text-sm text-[#6B4E12]" data-testid="payments-unavailable-notice">
              Online payments are being set up. Please call (502) 783-0430 to reserve in the meantime.
            </p>
          )}
          {card.error && <p className="mt-2 text-sm text-red-600" data-testid="card-error">{card.error}</p>}
        </div>
        <button type="submit" disabled={busy || !card.ready} data-testid="pay-button"
          className="w-full rounded bg-[#141210] py-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#F3DDA4] transition-colors hover:bg-[#2A2520] disabled:opacity-40">
          {busy ? "Processing…" : `Pay ${money(total)}`}
        </button>
        <p className="text-xs text-[#8A8076]">Payments are processed securely by Square. Your card details never touch our servers.</p>
      </form>
      <div className="lg:sticky lg:top-24 lg:self-start"><SummaryCard ev={ev} night={night} ticket={ticket} qty={qty} /></div>
    </main>
  );
}
